import { useState, useRef, useEffect, useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports -- direct call intentional: background polling for notification badge count; no user-visible toast needed
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import type {
  GetNotificationsInput,
  GetNotificationsOutput,
} from '@/types/notification.types';
import NotificationPopup from './NotificationPopup';
import { useAuth } from '@/hooks/useAuth';
import { HeaderProps } from '@/types/dashboard';
import { FloatingElement } from '@/components/AnimatedElements';
import { Avatar } from '@/components/ui/avatar';
import { useThemeColors } from '@/styles';
import { sizing, layout } from '@/styles/design-tokens';
import { Icon, IconName } from '@/icons';
import BoardSelectorPopup from './BoardSelectorPopup';
import { ShareButton } from '@/components/core/ShareButton';
import { DeepLinkEntity } from '@/utils/deep-linking';
import { WeekNavigator } from './WeekNavigator';
import {
  useLoginTransitionStore,
  setHeaderLogoRect,
} from '@/stores/loginTransitionStore';

export default function Header({
  onSidebarToggle,
  isSidebarCollapsed: _isSidebarCollapsed,
  breadcrumbs,
  onProfileClick,
  onLogoClick,
  currentBoardId,
  currentBoardName,
  onBoardChange,
  showWeekNavigator,
  selectedWeek,
  onWeekChange,
  showBoardSettings,
  onBoardSettingsClick,
  headerExtras,
}: HeaderProps) {
  const { user } = useAuth();
  const colors = useThemeColors();
  const headerLogoRef = useRef<HTMLButtonElement>(null);
  const transitionStore = useLoginTransitionStore();

  // Report header logo position for the merge animation.
  // Measures the <img> inside the button but uses the button's stable
  // vertical center to avoid float-animation offset.
  const reportLogoRect = useCallback(() => {
    if (headerLogoRef.current) {
      const img = headerLogoRef.current.querySelector('img');
      if (img) {
        const btnRect = headerLogoRef.current.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect();
        // Use button's stable vertical center, img's horizontal position & size
        const centerY = btnRect.top + (btnRect.height - imgRect.height) / 2;
        setHeaderLogoRect({
          x: imgRect.left,
          y: centerY,
          width: imgRect.width,
          height: imgRect.height,
        });
      }
    }
  }, []);

  // Measure after mount and on resize
  useEffect(() => {
    if (transitionStore.visible) {
      // Small delay to ensure the header is fully laid out
      const timer = setTimeout(reportLogoRect, 100);
      return () => clearTimeout(timer);
    }
  }, [transitionStore.visible, reportLogoRect]);

  // Hide the header logo during the center-to-header phase so
  // only the animated overlay logo is visible (avoids double logo)
  const hideHeaderLogo = transitionStore.phase === 'center-to-header';

  const [isHamburgerHovered, setIsHamburgerHovered] = useState(false);
  const [isHamburgerPressed, setIsHamburgerPressed] = useState(false);
  const [isProfileHovered, setIsProfileHovered] = useState(false);
  const [isProfilePressed, setIsProfilePressed] = useState(false);
  const [isBoardSelectorOpen, setIsBoardSelectorOpen] = useState(false);
  const [isBoardButtonHovered, setIsBoardButtonHovered] = useState(false);
  const [isBoardButtonPressed, setIsBoardButtonPressed] = useState(false);
  const [isSettingsHovered, setIsSettingsHovered] = useState(false);
  const [isSettingsPressed, setIsSettingsPressed] = useState(false);

  const [isNotificationPopupOpen, setIsNotificationPopupOpen] = useState(false);
  const [isNotificationHovered, setIsNotificationHovered] = useState(false);
  const [isNotificationPressed, setIsNotificationPressed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const result = await actionOrchestrator.execute<
        GetNotificationsInput,
        GetNotificationsOutput
      >(Action.GET_NOTIFICATIONS, { page: 1 });
      if (result.code === ResponseCode.SUCCESS && result.data) {
        setUnreadCount(result.data.unread_count);
      }
    } catch {
      // silent — badge is best-effort
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60_000);
    const onVisible = () => {
      if (!document.hidden) fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchUnreadCount]);

  const handleBoardSelect = (boardId: string) => {
    onBoardChange?.(boardId);
  };

  return (
    <header
      className="sticky top-0 z-40 shadow-soft"
      style={{
        height: layout.header.height,
        backgroundColor: colors.background,
        borderBottom: `1px solid ${colors.border.default}`,
      }}
    >
      <div
        className="flex items-center px-3 sm:px-6 gap-2"
        style={{ height: layout.header.height }}
      >
        {/* Left Section - Sidebar Toggle + Logo + Breadcrumbs */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onSidebarToggle}
            className="h-11 w-11 rounded-md transition-all duration-200 flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: isHamburgerPressed
                ? colors.active.background
                : isHamburgerHovered
                  ? colors.hover.background
                  : 'transparent',
              color: isHamburgerPressed
                ? colors.active.text
                : isHamburgerHovered
                  ? colors.hover.text
                  : colors.text.primary,
            }}
            onMouseEnter={() => setIsHamburgerHovered(true)}
            onMouseLeave={() => {
              setIsHamburgerHovered(false);
              setIsHamburgerPressed(false);
            }}
            onMouseDown={() => setIsHamburgerPressed(true)}
            onMouseUp={() => setIsHamburgerPressed(false)}
            aria-label="Toggle sidebar"
          >
            <svg
              style={{ width: sizing.icon.md, height: sizing.icon.md }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          {/* LearnBahn Logo + Text */}
          <button
            ref={headerLogoRef}
            onClick={onLogoClick}
            className="flex items-center gap-3 cursor-pointer"
            style={{ backgroundColor: 'transparent' }}
            aria-label="Go to Home"
            title="Go to Home"
          >
            <FloatingElement delay={0} duration={4} amplitude={3}>
              <img
                src="/LearnBahn.png"
                alt="LearnBahn"
                style={{
                  height: layout.header.logo,
                  opacity: hideHeaderLogo ? 0 : 1,
                  transition: 'opacity 0.1s ease',
                }}
                className="w-auto object-contain"
              />
            </FloatingElement>
            <span
              className="hidden xl:block text-lg font-semibold whitespace-nowrap"
              style={{ color: colors.text.primary }}
            >
              LearnBahn
            </span>
          </button>

          {/* Breadcrumbs (hidden on mobile) */}
          <nav className="hidden sm:flex items-center ml-2">
            <ol className="flex items-center space-x-2 text-base">
              {breadcrumbs.map((item, index) => (
                <li key={index} className="flex items-center">
                  {index > 0 && (
                    <svg
                      className="mx-2 h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      style={{ color: colors.border.light }}
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  <span
                    className="font-medium whitespace-nowrap"
                    onClick={item.onClick}
                    role={item.onClick ? 'button' : undefined}
                    style={{
                      color: item.isActive
                        ? colors.text.primary
                        : colors.text.secondary,
                      cursor: item.onClick ? 'pointer' : 'default',
                    }}
                  >
                    {item.label}
                  </span>
                </li>
              ))}
            </ol>
          </nav>
        </div>

        {/* Center Section - Week Navigator + Board Selector */}
        <div className="flex-1 flex items-center justify-end gap-2">
          {/* Week Navigator (only show when enabled) - Always visible when enabled */}
          {showWeekNavigator && selectedWeek && onWeekChange && (
            <div className="min-w-0 flex-shrink">
              <WeekNavigator
                selectedWeek={selectedWeek}
                onWeekChange={onWeekChange}
              />
            </div>
          )}

          {/* Screen-specific header actions (view toggle, sync button, etc.) */}
          {headerExtras && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {headerExtras}
            </div>
          )}

          {/* Board Selector - wrapped in overflow container for text truncation */}
          {onBoardChange && (
            <div className="flex items-center gap-2 min-w-0 flex-shrink overflow-hidden">
              <button
                onClick={() => setIsBoardSelectorOpen(true)}
                className="flex items-center gap-2 px-4 h-11 rounded-lg transition-all duration-200 font-medium text-base min-w-0"
                style={{
                  backgroundColor: isBoardButtonPressed
                    ? colors.active.background
                    : isBoardButtonHovered
                      ? colors.hover.background
                      : 'transparent',
                  color: isBoardButtonPressed
                    ? colors.active.text
                    : isBoardButtonHovered
                      ? colors.hover.text
                      : currentBoardName
                        ? colors.text.primary
                        : colors.text.tertiary,
                }}
                onMouseEnter={() => setIsBoardButtonHovered(true)}
                onMouseLeave={() => {
                  setIsBoardButtonHovered(false);
                  setIsBoardButtonPressed(false);
                }}
                onMouseDown={() => setIsBoardButtonPressed(true)}
                onMouseUp={() => setIsBoardButtonPressed(false)}
                aria-label={
                  currentBoardName
                    ? `Select board. Current: ${currentBoardName}`
                    : 'Select a board'
                }
                title={
                  currentBoardName
                    ? `Current board: ${currentBoardName}`
                    : 'Select a board'
                }
              >
                <Icon
                  name={IconName.DASHBOARD}
                  size="sm"
                  className="flex-shrink-0"
                />
                <span
                  className="hidden lg:inline truncate min-w-0"
                  style={{
                    maxWidth: 'calc(200px * var(--sizing-multiplier, 1))',
                  }}
                >
                  {currentBoardName || 'Select a board'}
                </span>
                <Icon
                  name={IconName.CHEVRON_DOWN}
                  size="sm"
                  className="flex-shrink-0"
                />
              </button>
              {currentBoardId && (
                <div className="flex-shrink-0">
                  <ShareButton
                    entity={DeepLinkEntity.BOARD}
                    id={currentBoardId}
                    ariaLabel="Copy board link"
                    queryParams={
                      selectedWeek
                        ? {
                            week: String(selectedWeek.week),
                            year: String(selectedWeek.year),
                          }
                        : undefined
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Board Selector Popup */}
        <BoardSelectorPopup
          isOpen={isBoardSelectorOpen}
          onClose={() => setIsBoardSelectorOpen(false)}
          currentBoardId={currentBoardId}
          onBoardSelect={handleBoardSelect}
        />

        {/* Right Section - Fixed position icons (Settings + Notification + Profile) */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Settings Button - Only show when board settings enabled */}
          {showBoardSettings && onBoardSettingsClick && (
            <button
              onClick={onBoardSettingsClick}
              className="h-11 w-11 rounded-md transition-all duration-200 flex items-center justify-center"
              style={{
                backgroundColor: isSettingsPressed
                  ? colors.active.background
                  : isSettingsHovered
                    ? colors.hover.background
                    : 'transparent',
                color: isSettingsPressed
                  ? colors.active.text
                  : isSettingsHovered
                    ? colors.hover.text
                    : colors.text.primary,
              }}
              onMouseEnter={() => setIsSettingsHovered(true)}
              onMouseLeave={() => {
                setIsSettingsHovered(false);
                setIsSettingsPressed(false);
              }}
              onMouseDown={() => setIsSettingsPressed(true)}
              onMouseUp={() => setIsSettingsPressed(false)}
              aria-label="Board settings"
              title="Board Settings"
            >
              <Icon name={IconName.SETTINGS} size="md" />
            </button>
          )}

          {/* Notification Button */}
          <button
            onClick={() => {
              setIsNotificationPopupOpen(true);
              setUnreadCount(0);
            }}
            className="relative h-11 w-11 rounded-md transition-all duration-200 flex items-center justify-center"
            style={{
              backgroundColor: isNotificationPressed
                ? colors.active.background
                : isNotificationHovered
                  ? colors.hover.background
                  : 'transparent',
              color: isNotificationPressed
                ? colors.active.text
                : isNotificationHovered
                  ? colors.hover.text
                  : unreadCount > 0
                    ? colors.error
                    : colors.text.primary,
              boxShadow:
                unreadCount > 0 ? `0 0 0 2px ${colors.error}50` : 'none',
            }}
            onMouseEnter={() => setIsNotificationHovered(true)}
            onMouseLeave={() => {
              setIsNotificationHovered(false);
              setIsNotificationPressed(false);
            }}
            onMouseDown={() => setIsNotificationPressed(true)}
            onMouseUp={() => setIsNotificationPressed(false)}
            aria-label="Open notifications"
            title="Notifications"
          >
            <Icon name={IconName.BELL} size="md" />
            {unreadCount > 0 && (
              <span
                className="absolute top-1.5 right-1.5 min-w-[16px] h-4 rounded-full flex items-center justify-center text-[10px] font-bold px-1 animate-pulse"
                style={{
                  backgroundColor: colors.error,
                  color: colors.text.inverse,
                }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <NotificationPopup
            isOpen={isNotificationPopupOpen}
            onClose={() => setIsNotificationPopupOpen(false)}
          />

          {/* User Info (hidden on mobile/tablet) */}
          <div className="hidden lg:flex flex-col text-right">
            <span
              className="text-sm font-medium whitespace-nowrap"
              style={{ color: colors.text.primary }}
            >
              {user?.display_name}
            </span>
            <span
              className="text-xs whitespace-nowrap"
              style={{ color: colors.text.secondary }}
            >
              {user?.organization_title}
            </span>
          </div>

          {/* Profile Button */}
          <button
            onClick={onProfileClick}
            className="h-10 w-10 sm:h-12 sm:w-12 rounded-full transition-all p-1 flex items-center justify-center"
            style={{
              backgroundColor: isProfilePressed
                ? colors.active.background
                : isProfileHovered
                  ? colors.hover.background
                  : 'transparent',
            }}
            onMouseEnter={() => setIsProfileHovered(true)}
            onMouseLeave={() => {
              setIsProfileHovered(false);
              setIsProfilePressed(false);
            }}
            onMouseDown={() => setIsProfilePressed(true)}
            onMouseUp={() => setIsProfilePressed(false)}
            aria-label={`Profile menu for ${user?.display_name}`}
            title={`${user?.display_name} - ${user?.organization_title}`}
          >
            <Avatar
              src={user?.profile_image}
              displayName={user?.display_name || 'User'}
              userId={user?.id || 'unknown'}
              size={44}
            />
          </button>
        </div>
      </div>
    </header>
  );
}
